import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AUTH_ERROR, type AdminSessionResponse } from "@shared";
import AdminApp from "../admin/AdminApp";
import { ApiFetchError, apiFetch, apiPost, NETWORK_ERROR_CODE } from "../api/client";
import LanguageSwitcher from "../components/LanguageSwitcher";

/**
 * Administration : une seule page, protegee par le mot de passe du .env
 * (CLAUDE.md section 5). Volontairement sobre — ce n'est pas un ecran de jeune,
 * et rien ici ne doit ressembler au jeu.
 *
 * Aucun lien depuis l'app du jeune ne mene ici : on y arrive en tapant /admin.
 */
export default function AdminHome() {
  const { t } = useTranslation();

  const [authenticated, setAuthenticated] = useState<boolean | undefined>(undefined);
  const [password, setPassword] = useState("");
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<AdminSessionResponse>("/admin/me")
      .then((data) => {
        if (!cancelled) setAuthenticated(data.admin);
      })
      .catch(() => {
        if (!cancelled) setAuthenticated(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    if (password === "" || busy) return;

    setBusy(true);
    setErrorKey(null);
    try {
      await apiPost<AdminSessionResponse>("/admin/login", { password });
      setPassword("");
      setAuthenticated(true);
    } catch (error) {
      if (error instanceof ApiFetchError && error.code === AUTH_ERROR.tooManyAttempts) {
        setErrorKey("admin.error.tooManyAttempts");
      } else if (error instanceof ApiFetchError && error.code === NETWORK_ERROR_CODE) {
        setErrorKey("admin.error.offline");
      } else {
        setErrorKey("admin.error.badPassword");
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    await apiPost<AdminSessionResponse>("/admin/logout").catch(() => undefined);
    setAuthenticated(false);
  }

  if (authenticated === undefined) {
    return (
      <main className="screen screen--admin screen--centered">
        <p className="card__text card__text--muted">{t("common.loading")}</p>
      </main>
    );
  }

  if (!authenticated) {
    return (
      <main className="screen screen--admin">
        <header className="screen__header">
          <h1 className="screen__title">{t("admin.title")}</h1>
        </header>

        <form className="card" onSubmit={(e) => void handleLogin(e)}>
          <label className="form__label" htmlFor="admin-password">
            {t("admin.passwordLabel")}
          </label>
          <input
            id="admin-password"
            className="form__input"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => {
              setPassword(e.target.value);
              setErrorKey(null);
            }}
          />

          {errorKey !== null && <p className="form__error">{t(errorKey)}</p>}

          <button
            type="submit"
            className="button button--primary button--full"
            disabled={password === "" || busy}
          >
            {busy ? t("admin.submitting") : t("admin.submit")}
          </button>
        </form>

        <footer className="screen__footer">
          <LanguageSwitcher />
        </footer>
      </main>
    );
  }

  return (
    <main className="screen screen--admin screen--wide">
      <header className="screen__header">
        <h1 className="screen__title">{t("admin.title")}</h1>
      </header>

      <AdminApp />

      <footer className="screen__footer">
        <button
          type="button"
          className="button button--ghost"
          onClick={() => {
            void handleLogout();
          }}
        >
          {t("admin.logout")}
        </button>
        <Link className="link" to="/">
          {t("admin.backToGame")}
        </Link>
        <LanguageSwitcher />
      </footer>
    </main>
  );
}
